/** The streak follows the TDG Account, not the machine.
 *
 *  `streak.ts` still owns the counting rule and still writes `bea.streak` on
 *  this device — nothing here changes how a day is counted, and a signed-out or
 *  offline reader keeps the streak they already have. What this module adds is
 *  the two things that make one number true on every device:
 *
 *  1. **Ownership.** `bea.streak` is a device record, and two accounts sharing a
 *     device must never share a streak — merging one reader's days into another
 *     account would invent days they never had, and would then travel to every
 *     device that account signs in on. So the record is stamped with the
 *     account that owns it. When somebody else signs in, the outgoing record is
 *     parked in a vault keyed by account and the incoming one is restored (or
 *     started blank), so neither reader's history leaks into the other's.
 *     The FIRST account to sign in after this shipped claims the device's
 *     existing record, which is what keeps an established streak from being
 *     reset by the very first sync.
 *
 *  2. **Convergence.** Sign-in, a newly counted day, coming back online, and
 *     returning to a backgrounded app each run pull → merge → push. The merge
 *     (`streakMerge.ts`) is a union of day runs, so it is commutative and
 *     idempotent: two devices pushing at once cannot lose a day, because each
 *     one keeps its own record and re-merges on the next pass until both hold
 *     the same union. That also means a device that has been shut for a month
 *     contributes its days when it comes back rather than overwriting anything.
 *
 *  Everything here fails soft. No network, no Supabase project, a device-local
 *  account, or a `tdg-core` without the streak table all end the same way: the
 *  device record is left exactly as it is and the reader never sees an error.
 *  A push that did not land sets `dirty`, and the next opportunity retries it. */
import { persisted } from '../core/state';
import { currentProfile } from './account/accountService';
import { VAULT_LIMIT, parkAndRestore } from './accountVault';
import { supabaseStreakRemote, type StreakRemote } from './account/remoteStreak';
import { BLANK_STREAK, recordDailyActivity, streak, type StreakState } from './streak';
import { isBlankStreak, mergeStreaks, sameStreak } from './streakMerge';

export interface StreakSyncState {
  /** Account whose streak `bea.streak` currently holds ('' = unclaimed, so the
   *  next account to sign in inherits the device's existing streak). */
  ownerId: string;
  /** Records parked for other accounts that have signed in on this device, so
   *  switching back restores a streak instead of starting from nothing. */
  vault: Record<string, StreakState>;
  /** The local record holds days the server has not accepted yet. */
  dirty: boolean;
  /** When a pull/push last succeeded (ms epoch, 0 = never). */
  syncedAt: number;
}

/** Device bookkeeping, deliberately NOT part of an account snapshot — see the
 *  `LS_SKIP` list in `account/sync.ts`. A backup file carrying another reader's
 *  parked streak would move their history onto a machine it never belonged to. */
export const streakSync = persisted<StreakSyncState>('streakSync', {
  ownerId: '', vault: {}, dirty: false, syncedAt: 0,
});

/** Don't re-pull on every return to the tab; a streak changes once a day. */
const REVISIT_GAP_MS = 5 * 60_000;
/** Let a burst of writes settle before pushing (a day rollover touches the
 *  record once, but sign-in can record a day moments after the merge). */
const PUSH_DEBOUNCE_MS = 2_500;

/** The backend this module talks to. A `let` so a non-Supabase backend, or a
 *  fake cloud in a test, is one assignment here rather than a rewrite of the
 *  rules below. The exported setter that used to sit under this was called by
 *  nothing — not even the suite it was written for. */
let remote: StreakRemote = supabaseStreakRemote;


/** Point `bea.streak` at the record belonging to `internalId`.
 *
 *  Three cases, and the middle one is the whole reason this exists:
 *  - **Already ours** — nothing to do.
 *  - **Unclaimed** — this device has a streak but has never been stamped, so
 *    the account signing in adopts it. That is the upgrade path, and it is what
 *    guarantees the first sync never resets an established streak.
 *  - **Someone else's** — park theirs under their id, restore ours from the
 *    vault (or start blank), and count today for the account that is actually
 *    signed in now.
 *
 *  Returns true when the live record was swapped. */
export function claimStreakFor(internalId: string, now = new Date()): boolean {
  if (!internalId) return false;
  const st = streakSync.get();
  if (st.ownerId === internalId) return false;
  if (!st.ownerId) {
    streakSync.set({ ...st, ownerId: internalId });
    return false;
  }
  const { vault, restored } = parkAndRestore(st.vault, st.ownerId, streak.get(), internalId, VAULT_LIMIT);
  streakSync.set({ ownerId: internalId, vault, dirty: false, syncedAt: 0 });
  streak.set(restored ? { ...restored } : { ...BLANK_STREAK });
  // The account that just signed in has not had today counted into the record
  // it just got back. `recordDailyActivity` is idempotent per day, so this is
  // safe whether or not the streak module's own sign-in handler has run yet.
  recordDailyActivity(now);
  return true;
}

/** Set while this module writes `streak`, so its own merge result does not read
 *  as a new day worth pushing. */
let applying = false;
let inflight: Promise<void> | null = null;
let pushTimer: number | undefined;
/** The account the last successful sync was for, so the two `currentProfile`
 *  emissions of a single sign-in (cached identity, then the account copy) do
 *  not each start a round trip. */
let syncedForId = '';

const online = (): boolean => typeof navigator === 'undefined' || navigator.onLine !== false;

/** Pull the account's record, merge it with this device's, keep the union on
 *  both sides. Never throws, never blocks the UI, and coalesces so overlapping
 *  triggers make one round trip. */
export function syncStreakNow(): Promise<void> {
  inflight ??= runSync().finally(() => { inflight = null; });
  return inflight;
}

async function runSync(): Promise<void> {
  const id = currentProfile.get()?.internalId ?? '';
  if (!id || !remote.available() || !online()) return;

  const owner = streakSync.get().ownerId;
  const stored = await remote.fetch(id);
  if (stored === null) return; // unreachable right now — the device record stands
  // Somebody signed out or switched accounts while the pull was in the air, so
  // `bea.streak` is no longer the record this answer belongs to. Dropping it is
  // the only safe move: writing it now would push one reader's days into
  // whoever is signed in at this moment.
  if (currentProfile.get()?.internalId !== id || streakSync.get().ownerId !== owner) return;

  // Read the local record AFTER the round trip: a day counted while the pull
  // was in the air belongs in the merge, not in the next one.
  const local = streak.get();
  const merged = mergeStreaks(local, stored);

  if (!sameStreak(merged, local)) {
    applying = true;
    try { streak.set(merged); } finally { applying = false; }
  }

  // Nothing to push when the server already holds the union, and nothing to
  // push when both sides are empty (a brand-new account creating an empty row
  // buys nothing).
  if (sameStreak(merged, stored) || isBlankStreak(merged)) {
    streakSync.set({ ...streakSync.get(), dirty: false, syncedAt: Date.now() });
    syncedForId = id;
    return;
  }
  const saved = await remote.save(id, merged);
  if (currentProfile.get()?.internalId !== id) return; // switched under us again
  streakSync.set({ ...streakSync.get(), dirty: !saved, syncedAt: saved ? Date.now() : streakSync.get().syncedAt });
  if (saved) syncedForId = id;
}

function schedulePush(): void {
  clearTimeout(pushTimer);
  pushTimer = window.setTimeout(() => { pushTimer = undefined; void syncStreakNow(); }, PUSH_DEBOUNCE_MS);
}

let started = false;

/** Wire the account streak up.
 *
 *  **Call this BEFORE `initStreak()`** (main.ts does). Both subscribe to
 *  `currentProfile`, and subscribers run in the order they were added: this one
 *  has to swap in the right account's record before `recordDailyActivity` writes
 *  today into it, or a day lands in the record of the reader who just signed
 *  OUT. Idempotent; call once. */
export function initStreakSync(): void {
  if (started) return;
  started = true;

  currentProfile.subscribe((p) => {
    if (!p) { syncedForId = ''; return; } // signing out leaves the device record working
    claimStreakFor(p.internalId);
    if (syncedForId !== p.internalId) void syncStreakNow();
  }, true);

  // A day counted here is a day the account's other devices should see.
  streak.subscribe(() => { if (!applying && currentProfile.get()) schedulePush(); });

  if (typeof window !== 'undefined' && window.addEventListener) {
    // Back online with a push that never landed, or days recorded offline.
    window.addEventListener('online', () => { if (currentProfile.get()) void syncStreakNow(); });
  }
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', () => {
      if (document.hidden || !currentProfile.get()) return;
      const { dirty, syncedAt } = streakSync.get();
      if (dirty || Date.now() - syncedAt > REVISIT_GAP_MS) void syncStreakNow();
    });
  }
}
